import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import { clearSessionEmployeeCache, resolveSessionEmployee } from '../../auth/application/sessionEmployeeResolver';
import { prepareLocalDataForOrganizationSwitch } from '../../shared/localDb';
import { resetRootRoute } from '../navigation/rootNavigation';
import { useAuthStore } from '../../auth/store/useAuthStore';
import { clearBootstrapSessionCache } from './bootstrapMobileSessionService';
import { awaitSessionBootstrap, clearSessionBootstrapGate } from './hydrateSessionCachesFromBootstrap';
import { runOrganizationResetHandlers } from '../../shared/ports/sessionResetPort';
import { setActiveOrganization, type ListMyOrganizationsPayload } from './organizationSwitcherService';
import { clearSessionOrganizationCache } from './sessionOrganization';
import { resolveInitialRootRouteName } from './routing/resolvePostLoginRoute';
import { useSessionStore } from './store/useSessionStore';

async function rebootstrapAfterOrganizationChange(
  userId: string,
  navigation?: NavigationProp<ParamListBase>,
  orgList?: ListMyOrganizationsPayload | null,
): Promise<boolean> {
  clearSessionBootstrapGate();
  clearBootstrapSessionCache();
  clearSessionOrganizationCache();
  clearSessionEmployeeCache();
  runOrganizationResetHandlers();
  await prepareLocalDataForOrganizationSwitch();
  useSessionStore.getState().resetSession();

  const payload = await awaitSessionBootstrap(userId, { force: true });
  if (!payload) return false;

  const branches = payload.branches ?? [];
  useSessionStore.getState().setBranches(branches);
  useSessionStore.getState().setCurrentBranch(undefined);

  if (navigation) {
    const activeBranchCount = branches.filter((b) => b.isActive !== false).length;
    const activeOrg = orgList?.organizations.find((o) => o.is_active);
    const isOwner = activeOrg?.is_owner === true;
    const employee = await resolveSessionEmployee(userId);
    const isEmployee = employee.isEmployee;
    const isGrant = employee.isGrant === true || activeOrg?.is_grant === true;

    if (activeBranchCount === 1) {
      const only = branches.find((b) => b.isActive !== false);
      if (only) {
        useSessionStore.getState().setCurrentBranch(only.id);
      }
    }

    const target = resolveInitialRootRouteName({
      activeBranchCount,
      isOwner,
      isEmployee,
      isGrant,
      currentBranchId: useSessionStore.getState().currentBranchId,
    });

    resetRootRoute(navigation, target);
  }

  return true;
}

/** Cambia la org activa en el backend, wipe SQLite, re-bootstrap y reset de sucursal. */
export async function performOrganizationSwitch(
  organizationId: string,
  navigation?: NavigationProp<ParamListBase>,
): Promise<boolean> {
  const userId = useAuthStore.getState().session?.user?.id;
  if (!userId) return false;

  const list = await setActiveOrganization(organizationId);
  if (!list) return false;

  return rebootstrapAfterOrganizationChange(userId, navigation, list);
}

/** Tras create_additional_organization (org ya activa en backend). */
export async function rebootstrapAfterAdditionalOrganization(
  navigation?: NavigationProp<ParamListBase>,
  orgList?: ListMyOrganizationsPayload | null,
): Promise<boolean> {
  const userId = useAuthStore.getState().session?.user?.id;
  if (!userId) return false;
  return rebootstrapAfterOrganizationChange(userId, navigation, orgList);
}
